/** Canonical pure semantics, shared by the host and native editor. */
import type { SceneGenSpec, SceneStartAnchorKind } from './scene-gen-spec-types';
import { stripZeroWidth } from '../templates/story-text';
import { stableJson } from '../stable-json';
import { isSubmittableSeed } from '../video-seed';


/**
 * ✍️ 자유 텍스트 축 상한 — 레인 SSOT(`story-chat-lane.SCENE_PROMPT_MAX_CHARS = 400`)와 같은 근거다:
 * 이 값들은 결국 생성 프롬프트로 실려 나가는데 EF 쪽에 길이 캡이 없어 클라가 잡는다. 캡슐은 프롬프트
 * 한 축이 아니라 여러 축을 싣고 code(JSON)에 영속하므로 400 보다 넉넉히 두되 무제한은 두지 않는다.
 */
export const GEN_SPEC_TEXT_MAX_CHARS = 1000;


/**
 * 📦 캡슐 전체의 직렬화 상한 — 미지 키 통과(아래 preserveSceneGenSpec)의 **안전 경계**다.
 * 미지 키를 무제한 통과시키면 저장분 하나가 code 를 부풀려 체크포인트·localStorage 쿼터를 깨뜨린다
 * (genVoice 의 data: base64 차단과 같은 부류의 사고). 초과분은 드롭하고 ★계측해서 돌려준다★.
 */
export const GEN_SPEC_MAX_JSON_CHARS = 4000;


/** 캡슐이 아는 키 — 이 목록 밖은 "미지 키"다(다음 웨이브 신설분·상위 버전). */
export const KNOWN_GEN_SPEC_KEYS: Readonly<Record<keyof SceneGenSpec, true>> = {
  action: true,
  actionSource: true,
  extraRequest: true,
  startAnchor: true,
  releasedCastName: true,
  locationRef: true,
  aiRef: true,
  durationSec: true,
  ambientSound: true,
  sfxNote: true,
  captionsInClip: true,
  consistency: true,
  stamp: true,
};


/**
 * ⛔ 값으로도 키로도 받지 않는 이름 — `out[k] = v` 계산 대입은 `__proto__` 에서 ★프로토타입을 바꾼다★
 * (JSON.parse 는 `__proto__` 를 own key 로 만들어 실제로 도달 가능한 경로다).
 */
export const FORBIDDEN_KEYS: ReadonlySet<string> = new Set(["__proto__", "constructor", "prototype"]);


/**
 * 시작 기준 어휘 화이트리스트 — 저장분을 신뢰하지 않는다(구 코드·손편집·상위 버전 모두 여기서 걸린다).
 * ★`Record<Union, true>` 로 선언한다★ — `Set<Union>` 은 오타만 잡고 **유니온이 넓어진 것은 못 잡는다**.
 *   그러면 새로 생긴 값이 컴파일 침묵 속에 저장 시점에서 조용히 폐기된다(가장 찾기 어려운 실패).
 *   레코드는 값이 하나만 늘어도 컴파일이 깨져 이 자리를 함께 고치게 만든다.
 */
export const START_ANCHOR_KINDS: Readonly<Record<SceneStartAnchorKind, true>> = {
  "prev-end": true,
  "character-main": true,
  upload: true,
  none: true,
};


export const isStartAnchorKind = (v: unknown): v is SceneStartAnchorKind =>
  typeof v === "string" && Object.prototype.hasOwnProperty.call(START_ANCHOR_KINDS, v);

/**
 * 소비자와 같은 관문(http(s))만 URL 로 인정 — 소비자가 무시할 값을 code 에 영속시키지 않는다.
 *
 * ★정규식이 아니라 `new URL()` 파서로 판정한다★ — 정규식 접두 검사는 스킴만 보고 나머지를 통과시켜서
 *   개행·제어문자가 박힌 문자열("https://a\n<script>")이 그대로 저장·프롬프트에 실린다. 파서는
 *   스킴을 정규화(대소문자 무관)하고 호스트 실재까지 확인한다.
 * ★반환은 원문(trim)★ — 파서의 정규화형(`u.href`)으로 바꿔 쓰면 저장된 URL 이 유저가 넣은 것과
 *   달라지고, 왕복 비교(specKey)가 매번 "값이 바뀌었다"로 흔들린다.
 */
export function httpUrl(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const t = v.trim();
  if (!t || CONTROL_CHARS.test(t)) return undefined;
  try {
    const u = new URL(t);
    if (u.protocol !== "http:" && u.protocol !== "https:") return undefined;
    return u.hostname ? t : undefined;
  } catch {
    return undefined;
  }
}


/** 개행·제어문자(TAB 포함) — URL·자유 텍스트 어디에도 원시로 실리면 안 된다(레포 인코딩 가드와 같은 축). */
export const CONTROL_CHARS = /[\u0000-\u001F\u007F]/;

/** 같은 문자류의 ★치환용★ 판(전역) — 거부가 아니라 **접기**를 하는 축이 쓴다(장소 설명 전문 · 2R P2-10). */
export const CONTROL_CHARS_GLOBAL = /[\u0000-\u001F\u007F]+/g;


/**
 * 공백만 있는 문자열은 "안 적었다"와 같다 — 빈 값을 저장하면 화면이 "설정됨"으로 거짓말한다.
 *
 * ★제로폭 문자를 먼저 걷어낸다★ — 레인 SSOT(`clipScenePrompt` 의 stripZeroWidth 선례)와 같은 이유다:
 *   U+200B 만 든 값은 trim 을 통과해 "설정됨"으로 보이지만 소비자에게는 빈 문자열이다(유령 설정).
 * ★길이 상한★ — 무제한이면 저장분 하나가 code 를 부풀리고 그대로 프롬프트에 실린다. 자르는 쪽이
 *   거부하는 쪽보다 낫다(유저 입력을 통째로 잃지 않는다).
 */
export function text(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const t = stripZeroWidth(v).trim();
  if (!t.length) return undefined;
  return t.length > GEN_SPEC_TEXT_MAX_CHARS ? t.slice(0, GEN_SPEC_TEXT_MAX_CHARS) : t;
}


/**
 * 🎭 시작 기준 `character-main` 이 결속하는 **캐스트 이름**의 상한 — `GEN_SPEC_TEXT_MAX_CHARS`(1000)를
 * 그대로 쓰지 않는다. 이 값은 자유 서술이 아니라 `data.cast[].name`(대본 속 호칭)이라 길어야 수십 자다.
 * ★자르는 쪽이 거부하는 쪽보다 낫다★ — 잘린 이름은 조회에서 **미스**로 떨어지고, 그 미스는 생성 전에
 *   고지된다(조용한 무시가 아니다). 통째로 버리면 왜 설정이 사라졌는지 말할 자리가 없다.
 */
export const GEN_SPEC_CAST_NAME_MAX_CHARS = 80;


/** 캐스트 이름 정규화 — `text()` 와 같은 관문(제로폭·trim·빈 값)에 이름 전용 상한만 다르다. */
export function castNameText(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const t = stripZeroWidth(v).trim();
  if (!t.length || CONTROL_CHARS.test(t)) return undefined;
  return t.length > GEN_SPEC_CAST_NAME_MAX_CHARS ? t.slice(0, GEN_SPEC_CAST_NAME_MAX_CHARS) : t;
}


/**
 * 🖼 캡슐 참조의 형상 — `SceneGenSpec.locationRef`/`aiRef` 의 **저장 어휘**(#2838 W-E).
 *
 * ★`desc` 는 `locationRef` 전용이다★ — 유저가 4방향 시트를 구우려고 쓴 **장소 설명 전문**이다.
 *   종전엔 그 문장이 이미지 생성에 한 번 쓰이고 버려졌고(`location-sheet-prompt`), 캡슐에는
 *   `label`(설명 앞 10자를 자른 칩 이름)만 남았다. 재생성 프롬프트가 볼 수 있는 장소 정보가
 *   `장소:오후 햇살이 드` 열 글자뿐이라, 그 지시는 앞뒤 맥락(시작 프레임·연속성 힌트·원본 씬)에
 *   구조적으로 밀렸다 — 세호 2차 QA 가 "핵심 특징 소실"로 관측한 것이 이 소실이다.
 * ★`aiRef` 에는 달지 않는다★ — 그 슬롯은 "역할을 지정하지 않는 일반 참조"라 배경 지시문을
 *   실을 자리가 없다(실으면 인물·소품 참조에 장소 문장이 붙는다). 그래서 관문에 축을 나눈다.
 *
 * ⚠️ **알려진 사각 — 구 번들 탭의 저장 1회에 `desc` 가 소멸한다**(2R P2-6 · 근거 반환).
 *   `desc` 는 **known key(`locationRef`) 안쪽**이라 {@link preserveSceneGenSpec} 의 미지 키 보존
 *   규약이 물지 않는다: 이 필드를 모르는 구 번들이 이 씬을 한 번 저장하면 그쪽 `imageRefValue` 가
 *   `{url,label}` 만 돌려주고 설명이 사라진다(결과는 **수리 이전 동작**으로의 복귀이지 새 파손은
 *   아니지만, 스탬프 지문이 한 번 뒤집혀 "설정 그대로" 고지가 1회 거짓말한다).
 * ★리뷰 권고(최상위 `locationDesc` 키 이동)를 이번 커밋에 넣지 않은 이유★: 최상위 키는
 *   {@link KNOWN_GEN_SPEC_KEYS}(`Record<keyof SceneGenSpec, true>`)에 등재돼야 하고 그건
 *   `SceneGenSpec` 타입 확장 = `agent/scene-gen-spec-types` 수정을 요구한다 — **이 레인의
 *   write-set 밖**(병렬 레인 충돌 방지 계약)이라 승인 없이 손대지 않는다. 덧붙여 최상위로 옮겨도
 *   보존은 **조건부**다: 미지 키는 캡슐 4,000자 상한에서 ★큰 것부터 먼저 버려지는★ 대상이라
 *   400자 설명이 1순위 드롭 후보가 된다. ⇒ 타입 확장 승인과 함께 후속 처리(회부).
 */
export interface CapsuleImageRef {
  url: string;
  label?: string;
  /** 🏙 장소 설명 전문(locationRef 전용 · 하위 호환: 부재 = 종전 동작 · 위 ⚠️ 사각 참조). */
  desc?: string;
}


/**
 * 🏙 장소 설명 전문의 상한 — `GEN_SPEC_TEXT_MAX_CHARS`(1000)를 그대로 쓰지 않는다.
 *
 * ★두 예산을 동시에 본다★: ①이 값은 재생성 프롬프트에 **그대로 실려 나간다**(레인 SSOT
 *   `SCENE_PROMPT_MAX_CHARS = 400` 과 같은 근거) ②캡슐 전체 직렬화 상한
 *   ({@link GEN_SPEC_MAX_JSON_CHARS} = 4000)은 **미지 키만** 잘라 낮추므로, 알려진 키가 혼자
 *   1000자를 먹으면 다음 웨이브의 축이 그만큼 먼저 밀린다.
 * ★자르는 쪽이 거부하는 쪽보다 낫다★ — 이 파일의 다른 상한들과 같은 태도다(잘린 설명도
 *   장소의 핵심 특징을 나르지만, 통째로 버리면 이 수리 자체가 성립하지 않는다).
 */
export const GEN_SPEC_LOCATION_DESC_MAX_CHARS = 400;


/**
 * ✂️ **코드포인트 단위 절단**(2R P2-7 · 어휘 SSOT `ref-binding.refAliasFromName` ④와 같은 근거).
 *
 * ★`slice(0, n)` 은 UTF-16 코드유닛을 자른다★ — 이모지·일부 한자의 서로게이트 쌍을 반으로 갈라
 *   **lone surrogate** 를 캡슐과 프롬프트에 함께 박는다(실측: `…G\ud83c`). 그 조각은 EF 매칭에서
 *   미스가 되고 JSON 왕복에서 치환문자가 된다.
 */
export function sliceCodePoints(s: string, max: number): string {
  const cp = [...s];
  return cp.length > max ? cp.slice(0, max).join("") : s;
}


/** 🖼 `{url,label?,desc?}` 형상 참조 — URL 없는 참조는 껍데기라 통째로 버린다. */
export function imageRefValue(raw: unknown, opts?: { withDesc?: boolean }): CapsuleImageRef | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const url = httpUrl(r.url);
  if (!url) return undefined;
  const label = text(r.label);
  /* ★`desc` 는 `text()` 와 같은 관문(제로폭·trim·빈 값)에 상한과 ★줄 접기★ 만 다르다★ — 여기서
     관문을 새로 쓰면 저장 시점과 소비 시점이 다른 문자열을 보게 된다(유령 설정의 발원지). */
  const rawDesc = opts?.withDesc === true && typeof r.desc === "string"
    ? stripZeroWidth(r.desc).trim()
    : "";
  /* 🧻 **개행·제어문자는 ★공백 하나로 접는다★**(2R P2-10 · `castNameText` 와 다른 처분인 이유).
     ★거부하지 않는다★: 이 값의 유입 경로는 화면 입력칸 하나가 아니다(3R P3-5 정정 — 종전 주석은
       "원천이 textarea"라고 적었으나 시트의 실제 컨트롤은 `input type="text"` 라 거짓이었다).
       진짜 방어 대상은 ①구 저장분·손편집 code ②다른 표면·상위 버전이 만든 캡슐 ③붙여넣기다 —
       전부 개행을 품을 수 있고, 거부하면 그 캡슐의 설명이 통째로 소실된다(조용한 실패 · 이 파일의
       "자르는 쪽이 거부하는 쪽보다 낫다" 태도와 같은 축).
     ★그대로 싣지도 않는다★: 이 값은 재생성 프롬프트에 실려 나가는데, 2.5 의 대사·바인딩 블록은
       ★줄 단위 문법★ 이라 원시 개행 하나가 블록 경계를 깨뜨린다(`castNameText` 가 제어문자를
       막는 것과 같은 목적, 다른 수단). 접은 뒤 상한을 잰다 — 순서가 뒤바뀌면 공백만 든 꼬리를
       상한 안에 남긴다. */
  const flatDesc = rawDesc.replace(CONTROL_CHARS_GLOBAL, " ").replace(/\s+/g, " ").trim();
  const desc = flatDesc.length ? sliceCodePoints(flatDesc, GEN_SPEC_LOCATION_DESC_MAX_CHARS) : undefined;
  return { url, ...(label ? { label } : {}), ...(desc ? { desc } : {}) };
}


/**
 * 🏙 **이 씬 캡슐이 들고 있는 장소 설명 전문**(#2838 W-E · 읽기 창구 하나).
 *
 * ★소비자가 `spec.locationRef.desc` 를 직접 만지지 않게 한다★ — `SceneGenSpec` 의 선언 타입은
 *   이 레인 밖(`lib/templates/story-template`)이 소유해서 아직 `desc` 를 모른다. 읽기를 한 자리로
 *   모아 두면 그 타입이 자란 날 이 함수 하나만 손대면 되고, 그때까지 캐스트도 여기 한 번뿐이다.
 * ★부재는 부재로 돌려준다★ — 빈 문자열을 만들면 소비자의 "설명이 있나" 판정이 참이 된다.
 */
export function sceneSpecLocationDesc(spec: SceneGenSpec | null | undefined): string | undefined {
  const ref = spec?.locationRef as CapsuleImageRef | undefined;
  const d = typeof ref?.desc === "string" ? ref.desc.trim() : "";
  return d.length ? d : undefined;
}


/**
 * 🧼 캡슐 정규화 — 읽기·쓰기·왕복 복원이 **같은 관문**을 통과하게 하는 단 하나의 판정기.
 *
 * ★빈 캡슐은 `undefined` 로 접는다★ — `{}` 는 부재와 뜻이 같은데, 저장하면 "설정이 있다"로 오독되는
 *   유령 상태가 된다(하위 호환의 정의는 "부재 = 종전 동작"이므로 두 표현이 갈리면 안 된다).
 */
export function sanitizeSceneGenSpec(raw: unknown): SceneGenSpec | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const out: SceneGenSpec = {};

  const action = text(r.action);
  if (action) out.action = action;
  const extraRequest = text(r.extraRequest);
  if (extraRequest) out.extraRequest = extraRequest;
  // 🏭 제작 출처 표식은 `action` 이 있을 때만 뜻이 있다(어휘는 "production" 하나 · 저장분 불신 관용구).
  if (out.action && r.actionSource === "production") out.actionSource = "production";
  /* 🪦 `transition`(연속/전환)은 은퇴한 축이다(결정 C 단일화 · story-template 주석 참조) — 여기서
     정규화하지 않는다. 낡은 저장분에 남아 있으면 아래 preserveSceneGenSpec 의 **미지 키 통과**가
     무해하게 나른다(읽는 소비자가 없으므로 거동 0). 되살리지 마라: 이 필드가 돌아오는 순간
     `연속 + 캐릭터 메인 샷` 같은 모순 조합이 다시 표현 가능해진다. */

  if (r.startAnchor && typeof r.startAnchor === "object" && !Array.isArray(r.startAnchor)) {
    const a = r.startAnchor as Record<string, unknown>;
    if (isStartAnchorKind(a.kind)) {
      const kind = a.kind;
      const url = httpUrl(a.url);
      /* 🎭 `castName` 은 `character-main` 전용 — 다른 kind 에 실린 이름은 아무도 안 읽는 dead data 라
         저장 시점에 떨군다(어휘 화이트리스트와 같은 태도: 소비자가 무시할 값은 영속시키지 않는다). */
      const castName = kind === "character-main" ? castNameText(a.castName) : undefined;
      /* ★이름 없는 `character-main` 은 **정당한 저장 형상**이다★(리드 결정 · 적대 1R) — "이 씬은
         캐릭터 메인샷으로 시작한다, 다만 아직 인물을 안 골랐다"는 중간 상태를 화면이 그대로 들고
         있어야 셀렉트가 튕기지 않는다(고르는 순간 저장이 드롭되면 폼이 옛 값으로 되돌아간다).
         그 상태의 **조달 거동**은 `sceneSpecAnchorMode` 가 `keep` 으로 정한다 — 저장 형상과 조달
         판정을 분리하는 것이 이 축의 계약이다(껍데기 방지는 조달 쪽에서 한다). */
      /* ★`none`·`character-main` 에 URL 을 남기지 않는다★ — `none` 은 "시작 고정 없음"과 "이 URL 로
         고정"이 한 값에 공존하면 결정 C 가 없애려던 이중 원천이 필드 안쪽에서 되살아나고,
         `character-main` 은 URL 을 굳히는 순간 그게 곧 **되덮기**다(캐스트에서 기준을 갈아끼워도
         씬은 옛 그림을 쓴다). 이 축의 원천은 이름 하나뿐이어야 한다. */
      out.startAnchor = {
        kind,
        ...(url && kind !== "none" && kind !== "character-main" ? { url } : {}),
        ...(castName ? { castName } : {}),
      };
    }
  }

  /* 🪦 삭제 자국(W3 후속 F07 ②) — `castName` 과 **같은 관문·같은 상한**을 쓴다(원천이 같은 이름이다).
     ★`startAnchor` 와 짝을 강제하지 않는다★: 자국은 "무엇 때문에 풀렸나"의 기록이라, 유저가 그 뒤
     다른 시작 기준을 고르는 도중의 중간 상태에서도 살아 있어야 한다(짝을 요구하면 그 창에서 자국이
     사라져 안내가 깜빡인다). 자국을 지우는 것은 저장 경로 하나가 소유한다(sceneSpecToSpec). */
  const releasedCastName = castNameText(r.releasedCastName);
  if (releasedCastName) out.releasedCastName = releasedCastName;

  // URL 없는 참조는 아무것도 가리키지 못한다(label 만 남기면 소비자가 못 쓰는 껍데기) — 두 축 공용 관문.
  // 🏙 `desc`(장소 설명 전문)는 locationRef 축에서만 통과한다 — aiRef 의 저장 바이트는 종전 그대로.
  const locationRef = imageRefValue(r.locationRef, { withDesc: true });
  if (locationRef) out.locationRef = locationRef;
  const aiRef = imageRefValue(r.aiRef);
  if (aiRef) out.aiRef = aiRef;

  // ⏱ 유한·양수만. 상한 clamp 없음(길이 축의 진실은 실측 — videoDurationSec 불변식과 같은 태도).
  if (typeof r.durationSec === "number" && Number.isFinite(r.durationSec) && r.durationSec > 0) {
    out.durationSec = r.durationSec;
  }

  /* 🌬 환경음은 **3상태**다(F13 · 타입 JSDoc 참조) — `null` 은 "명시 제거"라는 ★값★ 이므로
     `text()`(빈 값 = 미설정)로 접으면 안 된다. 그 순간 유저가 켠 [환경음 없음]이 저장 관문에서
     소멸해, 다시 열었을 때 체크가 풀려 있고 매니페스트 소리가 되살아난다.
     ★`null` 만 통과시킨다★ — `false`·`0`·`""` 같은 다른 falsy 는 3번째 상태의 어휘가 아니다
     (저장분을 신뢰하지 않는 관용구: 어휘 밖은 미설정으로 접는다). */
  if (r.ambientSound === null) out.ambientSound = null;
  else {
    const ambientSound = text(r.ambientSound);
    if (ambientSound) out.ambientSound = ambientSound;
  }
  const sfxNote = text(r.sfxNote);
  if (sfxNote) out.sfxNote = sfxNote;

  // 🔤🎬 영상 속 자막(2026-09-03 C안) — `true` 만 값이다(어휘 밖은 미설정 = 키 부재 · 저장분 불신 관용구).
  if (r.captionsInClip === true) out.captionsInClip = true;

  if (r.consistency && typeof r.consistency === "object" && !Array.isArray(r.consistency)) {
    const c = r.consistency as Record<string, unknown>;
    const consistency: NonNullable<SceneGenSpec["consistency"]> = {};
    // 🎲 seed 는 공급자에 그대로 실리는 값이라 ★제출·승계 관문 SSOT★ 를 그대로 쓴다(PR-1b).
    //   여기서 손으로 다시 좁히면 저장은 통과했는데 제출에서 조용히 떨어지는(또는 그 반대) 갈림이 난다.
    if (isSubmittableSeed(c.seed)) consistency.seed = c.seed;
    const anchorUrl = httpUrl(c.anchorUrl);
    if (anchorUrl) consistency.anchorUrl = anchorUrl;
    if (Object.keys(consistency).length) out.consistency = consistency;
  }

  // 🕰 생성 스탬프 — `key`(지문)가 없으면 스탬프 자체가 성립하지 않는다(판정은 오직 key 대조다).
  //   ★빈 문자열 key 도 유효한 값이다★ — "캡슐이 비어 있던 상태로 구웠다"는 정당한 기준선이라
  //   `text()`(공백=미설정)로 걸러선 안 된다. 그래서 여기서만 원시 string 판정을 쓴다.
  //   `at` 은 표시용이라 없거나 불량이면 0 으로 접는다(스탬프를 통째로 버리면 판정을 잃는다).
  if (r.stamp && typeof r.stamp === "object" && !Array.isArray(r.stamp)) {
    const st = r.stamp as Record<string, unknown>;
    if (typeof st.key === "string" && st.key.length <= GEN_SPEC_MAX_JSON_CHARS && !CONTROL_CHARS.test(st.key)) {
      const at = typeof st.at === "number" && Number.isFinite(st.at) && st.at >= 0 ? st.at : 0;
      /* 🖼 부분 소비 지문(이미지 축) — `key` 와 ★같은 관문★ 이다(둘 다 지문 문자열이라 판정 재료가 같다).
         ★부재와 빈 문자열을 구별해 통과시킨다★: `""` 는 "이미지가 소비할 축이 하나도 없는 상태로 구웠다"
         는 정당한 기준선이고, **부재**는 "이미지 배치가 아직 한 번도 안 구웠다" = 종전 `key` 판정으로
         되돌아가라는 신호다. 불량이면 통째로 떨군다(부재로 접혀 종전 판정이 된다 — fail-safe). */
      const imgKey = typeof st.imgKey === "string" && st.imgKey.length <= GEN_SPEC_MAX_JSON_CHARS && !CONTROL_CHARS.test(st.imgKey)
        ? st.imgKey : undefined;
      out.stamp = imgKey === undefined ? { key: st.key, at } : { key: st.key, at, imgKey };
    }
  }

  return Object.keys(out).length ? out : undefined;
}


/**
 * JSON 으로 안전하게 왕복하는 값인가 — 미지 키 통과의 **모양 관문**.
 * `JSON.stringify` 에 맡기면 함수·undefined 는 조용히 사라지고(모양이 바뀐다), 순환·BigInt 는 던지며,
 * 비유한수는 `null` 로 바뀐다. 그래서 값을 직접 걸어 JSON 원시값·배열·평범한 객체만 통과시킨다.
 * 깊이 상한은 순환 방어를 겸한다(깊이만큼만 내려가므로 순환이면 반드시 상한에 걸린다).
 */
export function isJsonSafe(v: unknown, depth = 0): boolean {
  if (depth > 6) return false;
  if (v === null || typeof v === "string" || typeof v === "boolean") return true;
  if (typeof v === "number") return Number.isFinite(v);
  if (Array.isArray(v)) return v.every((it) => isJsonSafe(it, depth + 1));
  if (typeof v !== "object") return false; // function·undefined·symbol·bigint
  if (Object.getPrototypeOf(v) !== Object.prototype && Object.getPrototypeOf(v) !== null) return false;
  return Object.entries(v as Record<string, unknown>).every(
    ([k, val]) => !FORBIDDEN_KEYS.has(k) && isJsonSafe(val, depth + 1),
  );
}


/** 승계·보존 결과 — 무엇을 버렸는지 함께 돌려준다(조용한 손실 금지). */
export interface GenSpecPreserveResult {
  /** 보존 payload — 알려진 키는 정규화형, 미지 키는 원형. 남는 게 없으면 `undefined`. */
  spec?: SceneGenSpec;
  /** 안전 경계(모양·금지 이름·크기)에 걸려 버린 미지 키 이름들 — 계측·고지용. */
  droppedKeys: string[];
}


/**
 * 🧬 **캡슐 보존 merge — 알려진 키는 정규화, 미지 키는 통과**(왕복·쓰기 공용 규약).
 *
 * ★왜 정규화만으로는 안 되는가★: `sanitizeSceneGenSpec` 은 화이트리스트라 **모르는 키를 버린다**.
 *   캡슐이 자란 뒤(Wave 2+ 신설 축) 구 번들을 든 탭이 편집을 한 번만 해도 그 축이 전 씬에서 소멸한다
 *   — 고지도 복구 수단도 없다(bgm 승계가 "값 정규화를 하지 않는다"고 못 박은 것과 같은 이유).
 * ★왜 통과만으로도 안 되는가★: 원형을 그대로 실으면 sanitize 가 거부한 값(javascript: URL·음수
 *   길이·거대 문자열)이 알려진 키 하나에 편승해 code 에 영구 박제된다. 둘을 **합쳐야** 한다.
 * ★미지 키의 안전 경계★: ①금지 이름(`__proto__` 등) ②JSON 안전 모양 ③캡슐 전체 직렬화 상한.
 *   상한 초과는 큰 것부터 버리고 버린 이름을 돌려준다(무엇을 잃었는지 말할 수 있어야 한다).
 */
export function preserveSceneGenSpec(raw: unknown): GenSpecPreserveResult {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { droppedKeys: [] };
  const r = raw as Record<string, unknown>;
  const known = sanitizeSceneGenSpec(raw);
  const merged: Record<string, unknown> = { ...(known ?? {}) };
  const droppedKeys: string[] = [];

  const unknownKeys: string[] = [];
  for (const k of Object.keys(r)) {
    if (Object.prototype.hasOwnProperty.call(KNOWN_GEN_SPEC_KEYS, k)) continue;
    if (FORBIDDEN_KEYS.has(k) || !isJsonSafe(r[k])) { droppedKeys.push(k); continue; }
    merged[k] = r[k];
    unknownKeys.push(k);
  }

  // 📦 크기 경계 — 큰 미지 키부터 버려 상한 아래로 내린다(알려진 키는 이미 필드별 상한이 있다).
  if (JSON.stringify(merged).length > GEN_SPEC_MAX_JSON_CHARS) {
    const bySize = [...unknownKeys].sort((a, b) => {
      const d = JSON.stringify(merged[b]).length - JSON.stringify(merged[a]).length;
      return d !== 0 ? d : a.localeCompare(b); // 크기 동률은 이름순 — 결정론
    });
    for (const k of bySize) {
      if (JSON.stringify(merged).length <= GEN_SPEC_MAX_JSON_CHARS) break;
      delete merged[k];
      droppedKeys.push(k);
    }
  }

  const spec = Object.keys(merged).length ? (merged as SceneGenSpec) : undefined;
  return spec ? { spec, droppedKeys } : { droppedKeys };
}


/**
 * ✍️ StoryTemplate 기준 캡슐 쓰기 — **새 template** 을 반환한다(입력 불변). 무변경이면 입력 참조 그대로.
 * 직렬화(code 문자열)는 호출부 몫이다 — 이 모듈은 `serializeStoryData` 를 모른다(순수·의존 최소).
 * ★gen 파생물에는 쓰지 않는다★ — 위 sceneGenSpecSupported 주석(진실 이중화·역반영 강등) 참조.
 */
/* ─────────────────────────── 🕰 생성 스탬프 · stale 판정(순수) ───────────────────────────
 * canvas-video-stamp(#1374)의 철학 그대로다: **지우지 말고 기록해서 대조한다**. 재생성 제출 순간의
 * 캡슐 지문을 남겨 두고, 지금 캡슐의 지문과 다르면 "화면과 설정이 어긋났다"고 말한다.
 * ★새 dirty 플래그를 만들지 않는다★ — 두 판정이 생기는 순간 배지와 점이 서로 다른 말을 한다.
 */

/**
 * 캡슐의 **비교 지문** — 스탬프 자신은 제외한다.
 * ★제외가 계약이다★: 스탬프를 지문에 넣으면 스탬프를 찍는 행위가 지문을 바꿔 버려 어떤 캡슐도
 *   영원히 stale 이 된다(자기 참조 붕괴). 부재·빈 캡슐은 빈 문자열 — "설정 없음"도 정당한 기준선이다.
 */
export function sceneGenSpecFingerprint(spec: SceneGenSpec | null | undefined): string {
  if (!spec) return "";
  const { stamp: _ignored, ...rest } = spec;
  return Object.keys(rest).length ? stableJson(rest) : "";
}


/**
 * 🕰 **지금 이 캡슐이 화면의 컷보다 새로운가**(= 재생성해야 반영되는 변경이 쌓였는가).
 * ★스탬프 부재 = false★ — 모르면 낡았다고 말하지 않는다(구 작업물 전 씬 주황 점 방지).
 * ★이것은 **영상 축을 포함한 전체 지문** 판정이다★ — 영상 재생성 창구가 있는 씬(=영상 씬)의 배지가
 *   본다. 이미지 씬은 창구가 없으므로 {@link isSceneGenSpecStaleForAxes} 로 소비 가능 축만 본다.
 */
export function isSceneGenSpecStale(spec: SceneGenSpec | null | undefined): boolean {
  if (!spec?.stamp) return false;
  return spec.stamp.key !== sceneGenSpecFingerprint(spec);
}


/* ── 🖼 부분 소비 축(이미지 경로) — 지문·스탬프·판정 3종 ─────────────────────────────────────
 * ★왜 축을 가르나★(수희 C절 2R P1): 스탬프는 종전 한 벌뿐이라 "이 캡슐 **전부**로 구웠다"만 말할 수
 *   있었다. 그런데 이미지 씬에는 그 전부를 굽는 창구(영상 재생성)가 **아예 없다** — 시트 버튼도
 *   핸들러도 사전 차단이다. 그래서 혼합 캡슐(텍스트 축 + 영상 축)이 붙은 이미지 씬은 찍을 주체가
 *   0개여서 배지가 영구 점등되고, 화면 문구대로 🖌 를 눌러도 안 꺼져 **재클릭 = 추가 과금**이었다.
 * ★축 목록은 이 모듈이 모른다★ — 무엇이 이미지 축인지는 소비자(`scene-spec-regen`)가 소유한다.
 *   여기서 목록을 또 적으면 소비(프롬프트에 싣는 축)와 판정(배지)이 갈린다. 그래서 인자로 받는다.
 */

/**
 * 🔑 **축을 투영한 지문** — `axes` 에 든 키만 남겨 {@link sceneGenSpecFingerprint} 와 ★같은 식★으로 만든다.
 * ★빈 투영 = 빈 문자열★(전체 지문의 규약과 동일) — "그 축으로는 담긴 설정이 없다"도 정당한 기준선이다.
 * `stamp` 는 지문 밖이라 축 목록에 들어와도 무시한다(자기 참조 붕괴 차단 — 전체 지문과 같은 계약).
 */
export function sceneGenSpecAxisFingerprint(
  spec: SceneGenSpec | null | undefined,
  axes: readonly string[],
): string {
  if (!spec) return "";
  const src = spec as unknown as Record<string, unknown>;
  const picked: Record<string, unknown> = {};
  for (const k of axes) {
    if (k === "stamp") continue;
    if (Object.prototype.hasOwnProperty.call(src, k)) picked[k] = src[k];
  }
  return Object.keys(picked).length ? stableJson(picked) : "";
}


/**
 * 🕰 **그 축만 놓고 볼 때 캡슐이 화면의 컷보다 새로운가** — 부분 소비 창구(이미지 씬)의 배지 판정.
 * ★스탬프 부재 = false★(전체 판정과 같은 태도 — 모르면 낡았다고 말하지 않는다).
 * ★`imgKey` 부재 = **종전 전체 판정으로 되돌아간다**★ — 구 작업물·아직 이미지 배치를 한 번도 안 거친
 *   캡슐의 화면은 1비트도 안 달라진다(회귀 0). 부분 지문은 이미지 배치가 실제로 구운 뒤에만 심긴다.
 */
export function isSceneGenSpecStaleForAxes(
  spec: SceneGenSpec | null | undefined,
  axes: readonly string[],
): boolean {
  if (!spec?.stamp) return false;
  const { imgKey } = spec.stamp;
  if (imgKey === undefined) return isSceneGenSpecStale(spec);
  return imgKey !== sceneGenSpecAxisFingerprint(spec, axes);
}


/**
 * ✅ **부분 소비 스탬프** — "이 축들은 이번 굽기가 반영했다"를 기록한다(전체 `key` 는 건드리지 않는다).
 *
 * ★전체 `key`·`at` 을 손대지 않는 것이 정직성이다★ — 이미지 굽기는 영상 축(길이·시작 기준·소리)을
 *   반영하지 못했으므로, 그 축까지 "구웠다"고 말하면 영상 씬이 됐을 때 배지가 거짓으로 꺼진다.
 * ★스탬프가 아예 없으면 아무것도 하지 않는다★ — 그 상태는 이미 "낡지 않음"이라 배지가 없고(막다른
 *   길도 없다), 여기서 스탬프를 새로 만들면 없던 판정을 발명하게 된다(입력 참조 그대로 반환).
 * ★무변경이면 입력 참조 그대로★ — 호출부가 참조 비교로 "쓸 것이 있나"를 판정해 저장 노이즈를 0으로 둔다.
 */
export function stampSceneGenSpecAxes(
  spec: SceneGenSpec | null | undefined,
  axes: readonly string[],
): SceneGenSpec | null | undefined {
  if (!spec?.stamp) return spec;
  const imgKey = sceneGenSpecAxisFingerprint(spec, axes);
  if (spec.stamp.imgKey === imgKey) return spec;
  return { ...spec, stamp: { ...spec.stamp, imgKey } };
}


/**
 * 🧾 **말할 가치가 있는 캡슐인가** — 고지 문구의 게이트(적대 리뷰 P3-9).
 * ★스탬프만 든 캡슐은 "설정"이 아니다★: 재생성 한 번이면 `{stamp}` 만 남은 캡슐이 생기는데, 그걸
 *   보고 "씬 설정이 사라졌어요"라고 말하면 유저가 잃은 것이 없는데도 잃었다고 듣는다(고지 스팸).
 *   판정은 stale 과 같은 지문 — 지문이 비었다 = 담긴 설정이 없다.
 */
export function hasMeaningfulSceneGenSpec(raw: unknown): boolean {
  const spec = preserveSceneGenSpec(raw).spec;
  if (!spec) return false;
  /* 🧑 **인물 미선택 메인샷만 든 캡슐도 "설정"이 아니다**(적대 2R P3) — 저장은 됐지만(지문은 바뀐다)
     조달 거동은 `keep` = 종전 그대로라, 재생성해도 화면이 1픽셀도 안 바뀐다. 그 상태를 "설정 있음"
     으로 세면 "씬 설정이 사라졌어요" 고지가 잃은 것 없는 유저에게 뜬다 — 스탬프만 든 캡슐을
     제외하는 것과 **같은 사유**다(고지 스팸 차단). ★지문에서 빼지는 않는다★: 저장은 실제로 일어났고,
     그 사실을 지문에서 지우면 되돌리기·왕복 비교가 거짓말을 하게 된다. */
  const { stamp: _ignored, ...rest } = spec;
  const keys = Object.keys(rest);
  if (keys.length === 1 && keys[0] === "startAnchor"
    && rest.startAnchor?.kind === "character-main" && !rest.startAnchor.castName?.trim()) {
    return false;
  }
  return sceneGenSpecFingerprint(spec) !== "";
}


/**
 * ✅ **재생성 제출 시점의 스탬프 찍기** — 이 캡슐로 구웠다고 기록한다(성공 커밋에서만 부른다).
 * 스탬프를 찍은 결과의 stale 은 정의상 false 다(지문이 곧 자기 자신).
 * `spec` 이 비어 있어도 스탬프만 든 캡슐을 돌려준다 — "설정 없이 구웠다"를 기억해야 다음 편집이
 * 정직하게 stale 이 된다(그 기억이 없으면 첫 편집이 조용히 반영된 척한다).
 */
export function stampSceneGenSpec(spec: SceneGenSpec | null | undefined, at: number): SceneGenSpec {
  const base = spec ?? {};
  const { stamp: _prev, ...rest } = base;
  const stampedAt = Number.isFinite(at) && at >= 0 ? at : 0;
  return { ...rest, stamp: { key: sceneGenSpecFingerprint(base), at: stampedAt } };
}


/**
 * 🧭 **기준선 스탬프** — 편집을 적용하기 ★직전★ 상태를 "구워진 설정"으로 못 박는다.
 *
 * ★왜 필요한가★: 스탬프 부재를 "stale 아님"으로 두면, 캡슐을 한 번도 안 쓴 작업물에서 유저가 상세
 *   설명을 적어도 배지가 안 뜬다(설정은 바뀌었는데 화면은 옛 컷 그대로 — 「적용 안 됐다」 오독의 정체).
 *   그래서 시트의 첫 쓰기가 **편집 전 지문**을 기준선으로 심는다. 그 순간부터 대조가 성립한다.
 * ★이미 스탬프가 있으면 손대지 않는다★ — 진짜 생성 기록을 편집이 덮으면 stale 이 영원히 안 뜬다.
 * ★`at: 0`★ = "실제 생성이 아니라 추정 기준선" 표식(진단에서 구별된다).
 */
export function baselineStampSceneGenSpec(spec: SceneGenSpec | null | undefined): SceneGenSpec | undefined {
  if (spec?.stamp) return spec;
  const base = spec ?? {};
  return { ...base, stamp: { key: sceneGenSpecFingerprint(base), at: 0 } };
}


/**
 * ✂️ **씬 분할 조각이 물려받을 캡슐** — `durationSec` 하나만 뺀 승계본(리드 결정 · W2).
 *
 * ★왜 길이만 빼는가★: 상세 설명·기타 요청·연결·시작 기준은 "이 장면을 어떻게 그리나"라 조각 양쪽에
 *   그대로 유효하다. 그러나 길이는 **씬 하나에 배정된 슬롯**이라 복제하면 두 조각이 각자 원래 길이를
 *   주장한다(분할=길이 불변 계약을 캡슐이 뒤에서 깨뜨린다). 분할 후 길이는 재설정 대상이다.
 * ★분할은 "설정을 바꾼 것"이 아니다★ — 그래서 stale 이 아니었던 캡슐은 새 지문으로 **다시 봉인**해
 *   유령 배지를 만들지 않는다. 반대로 이미 stale 이던 캡슐은 그 사실을 유지한다(진실을 지우지 않는다).
 * ★양쪽 분할 창구가 같은 함수를 쓴다★(splitStoryScene · splitStorySceneAtLine) — 규칙이 두 곳에
 *   흩어지면 어느 창구로 나눴는지에 따라 씬 설정이 달라진다.
 */
export function splitSceneGenSpecForPiece(raw: unknown): SceneGenSpec | undefined {
  const spec = preserveSceneGenSpec(raw).spec;
  if (!spec) return undefined;
  if (!Object.prototype.hasOwnProperty.call(spec, "durationSec")) return spec;
  const wasStale = isSceneGenSpecStale(spec);
  const { durationSec: _dropped, ...rest } = spec;
  const next = rest as SceneGenSpec;
  if (!Object.keys(next).length) return undefined;
  if (next.stamp && !wasStale) return { ...next, stamp: { key: sceneGenSpecFingerprint(next), at: next.stamp.at } };
  return next;
}

export type { SceneGenSpec, SceneStartAnchorKind } from './scene-gen-spec-types';
